import React, { useState } from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import Card from '@material-ui/core/Card'
import type { CardProps } from '@material-ui/core/Card'
import Popover from '@material-ui/core/Popover'
import Typography from '../Typography'
import CardShim from './CardShim'
import InfoIcon from '@material-ui/icons/Info'
import classnames from 'classnames'
import type { StyleClasses } from '../../styles'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    borderRadius: theme.borderRadius.xl
  },
  primaryValue: (props: StatCardProps) => {
    const length = (props.primaryValue ?? '').toString().length

    let fontSize = '2em'
    if (length >= 7) {
      fontSize = '1.2em'
    } else if (length >= 6) {
      fontSize = '1.4em'
    } else if (length >= 4) {
      fontSize = '1.5em'
    }

    return {
      fontVariantNumeric: 'tabular-nums',
      fontSize
    }
  },
  secondaryValue: (props: StatCardProps) => {
    const length = (props.secondaryValue ?? '').toString().length

    let fontSize = '1.3em'
    if (length >= 15) {
      fontSize = '0.9em'
    } else if (length >= 14) {
      fontSize = '1.0em'
    } else if (length >= 13) {
      fontSize = '1.1em'
    }

    return {
      fontVariantNumeric: 'tabular-nums',
      fontSize
    }
  },
  preserveHeight: {
    lineHeight: 2.9,
    [theme.breakpoints.only('md')]: {
      lineHeight: 3.5
    }
  },
  primaryTitle: {},
  secondaryTitle: {},
  primaryTitleBlock: {},
  infoIcon: {},
  popoverPaper: {}
}))

export interface StatCardProps {
  infoIcon?: boolean
  classes?: StyleClasses<typeof useStyles>
  style?: React.CSSProperties
  variant?: 'normal' | 'compact'
  loading?: boolean
  primaryTitle: React.ReactNode
  primaryValue: React.ReactNode
  secondaryTitle?: React.ReactNode
  secondaryValue?: React.ReactNode
  popoverAnchor?: 'left' | 'right'
  popoverContent?: React.ReactNode
  cardContent?: React.ReactNode
  cardProps?: CardProps
  showGamifiedCallouts?: boolean
}

function StatCard(props: StatCardProps) {
  const classes = useStyles(props)
  const {
    primaryTitle,
    secondaryTitle,
    loading,
    infoIcon,
    primaryValue,
    secondaryValue,
    style,
    popoverContent,
    cardProps
  } = props
  const variant = props.variant ? props.variant : 'normal'
  const popoverAnchor = props.popoverAnchor ? props.popoverAnchor : 'right'

  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null)
  const open = Boolean(popoverAnchorEl)

  const cardContent = props.cardContent ?? (
    <>
      <div className={classes.primaryTitleBlock}>
        <Typography variant="overline" gutterBottom={true}>
          <span className={classes.primaryTitle}>{primaryTitle}</span>
        </Typography>
        {infoIcon && <InfoIcon className={classes.infoIcon} />}
      </div>
      {loading ? (
        <CardShim />
      ) : (
        <>
          {variant === 'normal' ? (
            <Typography variant="h1">
              <span className={classes.primaryValue}>{primaryValue}</span>
            </Typography>
          ) : (
            <Typography variant="h3">
              <span className={classnames(classes.secondaryValue, classes.preserveHeight)}>{primaryValue}</span>
            </Typography>
          )}
        </>
      )}
      {secondaryTitle && (
        <Typography variant="overline" gutterBottom={true}>
          <span className={classes.secondaryTitle}>{secondaryTitle}</span>
        </Typography>
      )}
      {typeof secondaryValue !== 'undefined' &&
        (loading ? (
          <CardShim />
        ) : (
          <Typography variant="h3">
            <span className={classes.secondaryValue}>{secondaryValue}</span>
          </Typography>
        ))}
    </>
  )

  return (
    <>
      <Card classes={{ root: classes.root }} style={style} onClick={handleClick} {...cardProps}>
        {cardContent}
      </Card>
      {popoverContent && (
        <Popover
          classes={{ paper: classes.popoverPaper }}
          elevation={1}
          open={open}
          anchorEl={popoverAnchorEl}
          onClose={handleClose}
          anchorOrigin={
            popoverAnchor === 'right'
              ? {
                  vertical: 'center',
                  horizontal: 'right'
                }
              : { vertical: 'center', horizontal: 'left' }
          }
          transformOrigin={
            popoverAnchor === 'right'
              ? {
                  vertical: 'center',
                  horizontal: 'left'
                }
              : { vertical: 'center', horizontal: 'right' }
          }
        >
          {popoverContent}
        </Popover>
      )}
    </>
  )

  function handleClick(e: React.MouseEvent) {
    setPopoverAnchorEl(e.currentTarget)
  }

  function handleClose() {
    setPopoverAnchorEl(null)
  }
}

export default StatCard
